import { clsx } from 'clsx';
import Avatar from '@/components/common/Avatar';
import { UserRole } from '@/gql/enums';
import type { MemberFragment } from '@/gql/graphql';
import { renderAvatarName } from '@/utils/common';

interface MemberCardProps {
    member: MemberFragment;
    getRoleLabel: (role: UserRole) => string;
    isLast?: boolean;
}

const getRoleBadgeStyles = (role: UserRole) => {
    switch (role) {
        case UserRole.Trainee:
            return 'bg-neutral-50 text-gray-600 outline-gray-500/10';
        case UserRole.Trainer:
            return 'bg-blue-50 text-blue-700 outline-blue-600/10';
        case UserRole.CompanyAdmin:
            return 'bg-pink-50 text-pink-700 outline-pink-600/10';
        case UserRole.CourseAdmin:
        default:
            // Course Trainer, Admin, Super Admin
            return 'bg-green-50 text-green-700 outline-green-600/20';
    }
};

const MemberCard = ({ member, getRoleLabel, isLast = false }: MemberCardProps) => {
    const firstName = member.firstName || '';
    const lastName = member.lastName || '';
    const initials = renderAvatarName(firstName, lastName);
    const fullName = `${firstName} ${lastName}`.trim();

    const renderRoleBadges = () => {
        if (member.__typename === 'TenantUser' && member.roleAssignments?.length) {
            return member.roleAssignments.map(ra => (
                <div
                    key={ra.role}
                    className={`flex items-center rounded-md px-2 py-0.5 outline-1 outline-offset-[-1px] ${getRoleBadgeStyles(ra.role)}`}
                >
                    <div className="text-left text-sm leading-5 font-medium">{getRoleLabel(ra.role)}</div>
                </div>
            ));
        }
        return null;
    };

    return (
        <div className={clsx('flex items-start gap-2.5 p-5', !isLast && 'border-b-[1px] border-neutral-300')}>
            {/* Avatar */}
            <Avatar initials={initials} square={true} className="size-10 flex-shrink-0 bg-teal-100 text-teal-500" />

            {/* Name and Email */}
            <div className="flex flex-1 flex-col justify-center gap-1">
                <div className="leading-6 font-semibold text-neutral-900">{fullName}</div>
                <div className="leading-6 font-medium text-neutral-500">{member.email}</div>
                {/* Role Badge */}
                <div className="flex flex-wrap gap-2">{renderRoleBadges()}</div>
            </div>
        </div>
    );
};

export default MemberCard;
